import { ReactNode } from 'react'

import { i18n, MessageDescriptor } from '@lingui/core'

import { isSolanaChain, SupportedChainId } from '@cowprotocol/cow-sdk'
import { Currency } from '@cowprotocol/currency'
import { Command } from '@cowprotocol/types'
import { BackButton, Modal } from '@cowprotocol/ui'

import { msg, t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { Nullish } from 'types'

import { DisplayLink } from 'legacy/components/TransactionConfirmationModal/DisplayLink'
import { Order, OrderStatus } from 'legacy/state/orders/actions'

import { GnosisSafeTxDetails } from 'modules/account'
import { EthFlowStepper } from 'modules/ethFlow'
import { WatchAssetInWallet } from 'modules/wallet'

import { CowSwapAnalyticsCategory, toCowSwapGtmEvent } from 'common/analytics/types'
import { CancelButton } from 'common/pure/CancelButton'
import { ActivityDerivedState, ActivityStatus } from 'common/types/activity'
import { getIsBridgeOrder } from 'common/utils/getIsBridgeOrder'

import * as styledEl from './styled'

import { OrderProgressBarStepName } from '../../constants'
import { OrderProgressBarProps } from '../../types'
import { OrderProgressBar } from '../OrderProgressBar'
import { SolanaOrderStepper } from '../SolanaOrderStepper'
import { FinishedSuccessHeading } from '../steps/FinishedStep'

const activityStatusLabels: Partial<Record<ActivityStatus, MessageDescriptor>> = {
  [ActivityStatus.CONFIRMED]: msg`Confirmed`,
  [ActivityStatus.EXPIRED]: msg`Expired`,
  [ActivityStatus.CANCELLED]: msg`Cancelled`,
  [ActivityStatus.CANCELLING]: msg`Cancelling`,
  [ActivityStatus.FAILED]: msg`Failed`,
}

export interface TransactionSubmittedContentProps {
  onDismiss(): void
  hash: string | undefined
  chainId: SupportedChainId
  activityDerivedState: ActivityDerivedState | null
  currencyToAdd?: Nullish<Currency>
  orderProgressBarProps: OrderProgressBarProps
  navigateToNewOrderCallback?: (chainId: SupportedChainId, order?: Order, callback?: Command) => () => void
  successTitle?: ReactNode
}

// TODO: Break down this large function into smaller functions
// TODO: Reduce function complexity by extracting logic
// eslint-disable-next-line max-lines-per-function, complexity
export function TransactionSubmittedContent({
  onDismiss,
  chainId,
  hash,
  currencyToAdd,
  activityDerivedState,
  orderProgressBarProps,
  navigateToNewOrderCallback,
  successTitle,
}: TransactionSubmittedContentProps): ReactNode {
  const { order, isOrder, isCreating, isPending } = activityDerivedState || {}
  const { isProgressBarSetup, showCancellationModal, stepName, receiverEnsName } = orderProgressBarProps
  const showCancellationButton = isOrder && (isCreating || isPending) && showCancellationModal

  const isBridgeOrder = !!order && getIsBridgeOrder(order)
  const isSwapFilled = order?.status === OrderStatus.FULFILLED
  const isPresignaturePending = activityDerivedState?.isPresignaturePending
  const showSafeSigningInfo = isPresignaturePending && activityDerivedState && !!activityDerivedState.gnosisSafeInfo
  const showProgressBar = !showSafeSigningInfo && !isPresignaturePending && activityDerivedState && isProgressBarSetup
  const cancellationFailed = stepName === OrderProgressBarStepName.CANCELLATION_FAILED
  const isFinishedProgressStep =
    stepName === OrderProgressBarStepName.FINISHED || stepName === OrderProgressBarStepName.CANCELLATION_FAILED
  const isFinished =
    activityDerivedState?.status === ActivityStatus.CONFIRMED ||
    activityDerivedState?.status === ActivityStatus.EXPIRED ||
    cancellationFailed

  const progressBar = activityDerivedState && showProgressBar && isProgressBarSetup && (
    <OrderProgressBar
      {...orderProgressBarProps}
      order={order}
      navigateToNewOrder={navigateToNewOrderCallback?.(chainId, order, onDismiss)}
    />
  )

  if (isFinishedProgressStep && showProgressBar && order) {
    return (
      <Modal.Root>
        <Modal.Header
          onClose={onDismiss}
          headerContent={
            <FinishedSuccessHeading
              order={order}
              chainId={chainId}
              receiverEnsName={receiverEnsName}
              stepName={stepName}
              title={successTitle}
            />
          }
        />
        <Modal.Content>
          {progressBar}
          <WatchAssetInWallet
            shortLabel
            currency={currencyToAdd}
            data-click-event={toCowSwapGtmEvent({
              category: CowSwapAnalyticsCategory.PROGRESS_BAR,
              action: 'Click Watch Asset',
            })}
          />
        </Modal.Content>
      </Modal.Root>
    )
  }

  return (
    <styledEl.Wrapper>
      <styledEl.Section>
        <styledEl.Header>
          <BackButton
            onClick={onDismiss}
            data-click-event={toCowSwapGtmEvent({
              category: CowSwapAnalyticsCategory.PROGRESS_BAR,
              action: 'Click Back Arrow Button',
            })}
          />
          <styledEl.ActionsWrapper>
            {showCancellationButton && (
              <CancelButton
                onClick={showCancellationModal}
                data-click-event={toCowSwapGtmEvent({
                  category: CowSwapAnalyticsCategory.PROGRESS_BAR,
                  action: 'Click Cancel Order',
                })}
              >
                <Trans>Cancel</Trans>
              </CancelButton>
            )}
            <DisplayLink
              id={hash}
              chainId={chainId}
              leadToBridgeTab={isBridgeOrder && isSwapFilled}
              data-click-event={toCowSwapGtmEvent({
                category: CowSwapAnalyticsCategory.PROGRESS_BAR,
                action: 'Click Transaction Link',
              })}
            />
          </styledEl.ActionsWrapper>
        </styledEl.Header>
        <>
          {!isProgressBarSetup && <styledEl.Title>{getTitleStatus(activityDerivedState)}</styledEl.Title>}
          {showSafeSigningInfo && <GnosisSafeTxDetails chainId={chainId} activityDerivedState={activityDerivedState} />}
          {!isFinished &&
            (isSolanaChain(chainId) ? (
              <SolanaOrderStepper order={order} />
            ) : (
              <EthFlowStepper order={order} showProgressBar={!!showProgressBar} />
            ))}
          {progressBar}
          <styledEl.ButtonGroup>
            <WatchAssetInWallet
              shortLabel
              currency={currencyToAdd}
              data-click-event={toCowSwapGtmEvent({
                category: CowSwapAnalyticsCategory.PROGRESS_BAR,
                action: 'Click Watch Asset',
              })}
            />

            {activityDerivedState?.status === ActivityStatus.PENDING && !isProgressBarSetup && (
              <styledEl.ButtonCustom
                onClick={onDismiss}
                data-click-event={toCowSwapGtmEvent({
                  category: CowSwapAnalyticsCategory.PROGRESS_BAR,
                  action: 'Click Close',
                })}
              >
                <Trans>Close</Trans>
              </styledEl.ButtonCustom>
            )}

            {isFinished && (
              <styledEl.ButtonSecondary
                onClick={onDismiss}
                data-click-event={toCowSwapGtmEvent({
                  category: CowSwapAnalyticsCategory.PROGRESS_BAR,
                  action: 'Click Close',
                })}
              >
                <Trans>Close</Trans>
              </styledEl.ButtonSecondary>
            )}
          </styledEl.ButtonGroup>
        </>
      </styledEl.Section>
    </styledEl.Wrapper>
  )
}

function getTitleStatus(activityDerivedState: ActivityDerivedState | null): string {
  if (!activityDerivedState) {
    return ''
  }

  const prefix = activityDerivedState.isOrder ? t`Order` : t`Transaction`
  const postfix = i18n._(activityStatusLabels[activityDerivedState.status] as MessageDescriptor) || t`Submitted`

  return `${prefix} ${postfix}`
}
